/**
 * 电商套图 · 管理后台项目列表 Tab（分页 + 筛选 + 详情 + 删除）
 */
import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Eye, Loader2, Search, Trash2 } from 'lucide-react';
import { platformFetch } from '../../../platform/http';
import toast from '../../Toast';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';

interface SuiteProjectRow {
  id: string;
  title: string;
  userEmail: string;
  userName: string | null;
  status: string;
  productName: string | null;
  targetPlatform: string | null;
  shotCount: number;
  createdAt: string;
  updatedAt: string;
}

interface ProjectDetail {
  id: string;
  title: string;
  status: string;
  styleLock: string | null;
  shots: Array<{
    id: string;
    displayName: string;
    status: string;
    reviewStatus: string;
    protectionMode: string;
    generation: number;
    outputImage: string | null;
    errorMessage: string | null;
  }>;
}

const STATUS_LABELS: Record<string, string> = {
  draft: '未规划',
  planned: '已规划',
  generating: '生成中',
  partial: '部分完成',
  completed: '已完成',
};

const REVIEW_LABELS: Record<string, string> = {
  pending: '待审核',
  approved: '已通过',
  needs_redo: '待重做',
};

export default function EcommerceSuiteProjectsTab() {
  const [projects, setProjects] = useState<SuiteProjectRow[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [userEmailFilter, setUserEmailFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchProjects = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (userEmailFilter) params.set('userEmail', userEmailFilter);
      if (statusFilter) params.set('status', statusFilter);
      const res = await platformFetch(`/admin/ecommerce-suite/projects?${params}`);
      const data = await res.json();
      setProjects(data.data || []);
      if (data.pagination) setPagination(data.pagination);
    } catch (err) {
      console.error('获取套图项目列表失败:', err);
      toast.error('获取项目列表失败');
    } finally {
      setLoading(false);
    }
  }, [userEmailFilter, statusFilter]);

  useEffect(() => {
    fetchProjects(1);
    // 仅首次加载；筛选变化通过表单提交触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (id: string) => {
    try {
      setDetailLoading(true);
      const res = await platformFetch(`/admin/ecommerce-suite/projects/${id}`);
      const data = await res.json();
      setDetail(data.data);
    } catch {
      toast.error('获取项目详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('确定删除该项目？')) return;
    try {
      const res = await platformFetch(`/admin/ecommerce-suite/projects/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      toast.success('项目已删除');
      fetchProjects(pagination.page);
    } catch {
      toast.error('删除失败');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl p-4 shadow-lg border border-light-border flex flex-wrap items-center gap-3">
        <input
          value={userEmailFilter}
          onChange={(e) => setUserEmailFilter(e.target.value)}
          placeholder="按用户邮箱筛选"
          className="rounded-xl border border-light-border px-3 py-2 text-sm text-light-text outline-none focus:border-accent-blue"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-xl border border-light-border px-3 py-2 text-sm text-light-text outline-none focus:border-accent-blue"
        >
          <option value="">全部状态</option>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => fetchProjects(1)}
          className="inline-flex items-center gap-1.5 rounded-xl bg-accent-blue px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
        >
          <Search className="h-4 w-4" />
          查询
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-light-border text-light-muted">
                <th className="px-4 py-3 text-left font-medium">项目</th>
                <th className="px-4 py-3 text-left font-medium">用户</th>
                <th className="px-4 py-3 text-left font-medium">状态</th>
                <th className="px-4 py-3 text-left font-medium">分镜数</th>
                <th className="px-4 py-3 text-left font-medium">创建时间</th>
                <th className="px-4 py-3 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-accent-blue mx-auto" /></td></tr>
              ) : projects.length === 0 ? (
                <tr><td colSpan={6} className="py-10 text-center text-light-muted">暂无数据</td></tr>
              ) : (
                projects.map((project) => (
                  <tr key={project.id} className="border-b border-light-border/60 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-light-text">{project.title}</p>
                      <p className="text-xs text-light-muted">{project.productName || '—'}{project.targetPlatform ? ` · ${project.targetPlatform}` : ''}</p>
                    </td>
                    <td className="px-4 py-3 text-light-text">
                      <p>{project.userEmail}</p>
                      {project.userName && <p className="text-xs text-light-muted">{project.userName}</p>}
                    </td>
                    <td className="px-4 py-3 text-light-text">{STATUS_LABELS[project.status] || project.status}</td>
                    <td className="px-4 py-3 text-light-text">{project.shotCount}</td>
                    <td className="px-4 py-3 text-light-muted text-xs">{new Date(project.createdAt).toLocaleString('zh-CN')}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button type="button" onClick={() => openDetail(project.id)} className="rounded-lg p-1.5 text-light-muted transition hover:text-accent-blue" title="查看详情">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => handleDelete(project.id)} className="rounded-lg p-1.5 text-light-muted transition hover:text-red-500" title="删除">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-light-border px-4 py-3">
            <span className="text-xs text-light-muted">共 {pagination.total} 条 · 第 {pagination.page}/{pagination.totalPages} 页</span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => fetchProjects(pagination.page - 1)}
                className="rounded-lg border border-light-border p-1.5 text-light-text disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => fetchProjects(pagination.page + 1)}
                className="rounded-lg border border-light-border p-1.5 text-light-text disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {detailLoading && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-zinc-950/40">
          <Loader2 className="h-8 w-8 animate-spin text-white" />
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <button aria-label="关闭" className="absolute inset-0 bg-zinc-950/60" onClick={() => setDetail(null)} />
          <div className="relative max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-light-text">{detail.title}</h3>
                <p className="text-xs text-light-muted mt-1">
                  {STATUS_LABELS[detail.status] || detail.status}{detail.styleLock ? ` · 风格锁：${detail.styleLock}` : ''}
                </p>
              </div>
              <button type="button" onClick={() => setDetail(null)} className="text-light-muted hover:text-light-text">✕</button>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {detail.shots.map((shot) => (
                <div key={shot.id} className="rounded-xl border border-light-border p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-light-text truncate">{shot.displayName}</p>
                    <span className="text-xs text-light-muted shrink-0">{REVIEW_LABELS[shot.reviewStatus] || shot.reviewStatus}</span>
                  </div>
                  {shot.outputImage ? (
                    <img src={resolveBrowserMediaUrl(shot.outputImage)} alt={shot.displayName} className="mt-2 aspect-square w-full rounded-lg object-cover" />
                  ) : (
                    <div className="mt-2 flex aspect-square w-full items-center justify-center rounded-lg bg-gray-50 text-xs text-light-muted">
                      {shot.status === 'generating' ? '生成中' : shot.errorMessage || '未生成'}
                    </div>
                  )}
                  <p className="mt-2 text-xs text-light-muted">
                    {shot.protectionMode === 'PIXEL_PROTECTED' ? '像素保护' : '创意'} · 第 {shot.generation} 版
                  </p>
                </div>
              ))}
              {detail.shots.length === 0 && <p className="text-sm text-light-muted">该项目暂无分镜</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
